// Screen 10 — P&L summary.
//
// Four reconciliation stat cards (Total Client Top-Up, Nexova Commission, Total
// Agency Paid, Net Profit) each marked "Reconciled to seed", a green banner when
// the independent-column reconcile matches to the cent, four per-month charts
// (top-ups line, commission bar, agency-paid line, net-profit bar) derived from
// the monthly P&L rollup, and an account-status donut summed from the per-client
// pipeline counts. A Month / Quarter / YTD segmented control sets the period
// (the request carries the period and the screen re-fetches).
//
// MONEY: stat-card values are the exact decimal strings the backend returns
// (reconcile.computed). The chart series are converted to plain numbers for
// plotting ONLY (axes are presentational); the authoritative figures stay as
// strings in the cards.
//
// REAL SHAPES (admin Go handlers — source of truth):
//   GET /admin/pnl/summary    -> { months: PnlMonth[], reconcile: { computed, expected, matches } }
//   GET /admin/charts/topup   -> { series: { year, week, entity_id, total_topup }[] }
//   GET /admin/accounts/summary -> { counts: { client_id, agency_id, total, requested, whitelisted, paused, banned }[] }
// The donut is summed from counts[]; the per-month charts come from months[]
// (gross_topup / nexova_commission / agency_paid / net_profit). There is NO
// `anchors` / `active` / `in_review` in the real API — those were MSW-only.

import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  BarChart,
  Donut,
  LineChart,
  StatCard,
  type DonutSlice,
  type MoneyTone,
} from "@/components/nexus";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";
import { Segmented } from "./controls";

// --- Real backend shapes -----------------------------------------------------
interface PnlMonth {
  year: number;
  month: number;
  gross_topup: string;
  nexova_commission: string;
  agency_paid: string;
  agency_commission: string;
  net_profit: string;
  retained_margin: string;
}
interface ReconcileColumns {
  total_client_topup: string;
  nexova_commission: string;
  total_agency_paid: string;
  net_profit: string;
}
interface PnlSummary {
  months: PnlMonth[];
  reconcile: {
    computed: ReconcileColumns;
    expected: ReconcileColumns;
    matches: boolean;
  };
}
interface ChartsBody {
  series: { year: number; week: number; entity_id: string; total_topup: string }[];
}
interface AccountCount {
  client_id: string;
  agency_id: string;
  total: number;
  requested: number;
  whitelisted: number;
  paused: number;
  banned: number;
}
interface AccountsSummary {
  counts: AccountCount[];
}

type Period = "month" | "quarter" | "ytd";

const ANCHOR_TONE: Record<string, MoneyTone> = {
  client_topup: "credit",
  commission: "profit",
  agency_paid: "debit",
  net_profit: "profit",
};

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Convert a money-string series to plot numbers (presentational only).
function toNums(series: string[]): number[] {
  return series.map((s) => Number(s));
}

export function PnlPage() {
  const [period, setPeriod] = useState<Period>("month");
  const [summary, setSummary] = useState<PnlSummary | null>(null);
  const [charts, setCharts] = useState<ChartsBody | null>(null);
  const [accounts, setAccounts] = useState<AccountsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [s, c, a] = await Promise.all([
          api.get<PnlSummary>(`/admin/pnl/summary?period=${period}`),
          api.get<ChartsBody>(`/admin/charts/topup?period=${period}`),
          api.get<AccountsSummary>("/admin/accounts/summary"),
        ]);
        if (cancelled) return;
        setSummary(s);
        setCharts(c);
        setAccounts(a);
      } catch (err) {
        if (!cancelled) setError(errMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  // The four reconciliation anchors are read straight off reconcile.computed
  // (exact decimal strings). "reconciled" per-anchor = the whole column matched.
  const reconcile = summary?.reconcile;
  const matches = reconcile?.matches ?? false;
  const anchors = reconcile
    ? [
        { key: "client_topup", label: "Total Client Top-Up", value: reconcile.computed.total_client_topup },
        { key: "commission", label: "Nexova Commission", value: reconcile.computed.nexova_commission },
        { key: "agency_paid", label: "Total Agency Paid", value: reconcile.computed.total_agency_paid },
        { key: "net_profit", label: "Net Profit", value: reconcile.computed.net_profit },
      ]
    : [];

  // Per-month chart series come from the P&L rollup (ordered oldest→newest).
  const months = summary?.months ?? [];
  const monthLabels = months.map(
    (m) => MONTH_LABELS[(m.month - 1 + 12) % 12] ?? String(m.month),
  );

  // Account-status donut: sum the per-client/agency pipeline counts.
  const accTotals = (accounts?.counts ?? []).reduce(
    (acc, c) => ({
      requested: acc.requested + c.requested,
      whitelisted: acc.whitelisted + c.whitelisted,
      paused: acc.paused + c.paused,
      banned: acc.banned + c.banned,
      total: acc.total + c.total,
    }),
    { requested: 0, whitelisted: 0, paused: 0, banned: 0, total: 0 },
  );
  const donutSlices: DonutSlice[] = [
    { label: "whitelisted", value: accTotals.whitelisted, tone: "credit" },
    { label: "requested", value: accTotals.requested, tone: "stale" },
    { label: "paused", value: accTotals.paused, tone: "warn" },
    { label: "banned", value: accTotals.banned, tone: "danger" },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">P&amp;L summary</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            The four anchors reconciled to the imported seed, to the cent.
          </p>
        </div>
        <Segmented<Period>
          value={period}
          onChange={setPeriod}
          options={[
            { value: "month", label: "Month" },
            { value: "quarter", label: "Quarter" },
            { value: "ytd", label: "YTD" },
          ]}
        />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load the P&amp;L</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading || !summary || !charts || !accounts ? (
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="grid gap-5">
          {matches && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-lg border border-nx-credit/30 bg-nx-credit-soft px-4 py-2.5 text-sm font-medium text-nx-credit"
            >
              <CheckCircle2 className="size-4" />
              Reconciliation passed — all four anchors match to the cent.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {anchors.map((a) => (
              <StatCard
                key={a.key}
                label={a.label}
                value={`$${a.value}`}
                tone={ANCHOR_TONE[a.key] ?? "default"}
                meta={
                  matches ? (
                    <span className="text-nx-credit">Reconciled to seed</span>
                  ) : (
                    <span className="text-nx-debit">Mismatch</span>
                  )
                }
              />
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Client top-ups"
              caption="Per month"
              chart={
                <LineChart
                  data={toNums(months.map((m) => m.gross_topup))}
                  color="var(--nx-credit)"
                />
              }
              months={monthLabels}
            />
            <ChartCard
              title="Nexova commission"
              caption="Per month"
              chart={
                <BarChart
                  data={toNums(months.map((m) => m.nexova_commission))}
                  color="var(--nx-profit)"
                />
              }
              months={monthLabels}
            />
            <ChartCard
              title="Agency paid"
              caption="Per month"
              chart={
                <LineChart
                  data={toNums(months.map((m) => m.agency_paid))}
                  color="var(--nx-debit)"
                />
              }
              months={monthLabels}
            />
            <ChartCard
              title="Net profit"
              caption="Per month"
              chart={
                <BarChart
                  data={toNums(months.map((m) => m.net_profit))}
                  color="var(--nx-brand)"
                />
              }
              months={monthLabels}
            />
          </div>

          <Card className="gap-3 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-sm font-medium">
                Account status
              </h3>
              <span className="font-mono text-xs text-nx-text-3 tabular-nums">
                {accTotals.total} total
              </span>
            </div>
            <Donut slices={donutSlices} />
          </Card>
        </div>
      )}
    </AdminShell>
  );
}

function ChartCard({
  title,
  caption,
  chart,
  months,
}: {
  title: string;
  caption: string;
  chart: React.ReactNode;
  months: string[];
}) {
  return (
    <Card className="gap-3 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-medium">{title}</h3>
        <span className="text-xs text-nx-text-3">{caption}</span>
      </div>
      {chart}
      <div className="flex justify-between text-[0.7rem] text-nx-text-3">
        {months.map((m, i) => (
          <span key={`${m}-${i}`}>{m}</span>
        ))}
      </div>
    </Card>
  );
}
